import { z } from 'zod'
import { ValueObject } from '@lukso/controllerapps-sharedlogic-ddd-core'
import { ChainID } from '@lukso/controllerapps-sharedlogic-primitives'

import { Controller } from './Controller'
import { ProfileAddress } from './ProfileAddress'
import { ProfileName } from './ProfileName'
import { DeviceControllerAddress } from './DeviceControllerAddress'

const ProfilePropsSchema = z.object({
  name: z.instanceof(ProfileName).optional(),
  address: z.instanceof(ProfileAddress),
  chainID: z.instanceof(ChainID),
  controllers: z.array(z.instanceof(Controller)),
  deviceControllerAddress: z.instanceof(DeviceControllerAddress),
})

type ProfileProps = z.infer<typeof ProfilePropsSchema>

export class Profile extends ValueObject<ProfileProps> {
  public getName(): ProfileName | undefined {
    return this.props.name
  }

  /**
   * Returns the address of this profile.
   */
  public getAddress(): ProfileAddress {
    return this.props.address
  }

  /**
   * Returns the chain ID of this profile.
   */
  public getChainID(): ChainID {
    return this.props.chainID
  }

  /**
   * @returns The controllers associated with this profile. This list returns whatever is stored onchain, so it may
   * contain duplicates.
   */
  public getControllers(): Controller[] {
    return this.props.controllers
  }

  /**
   * @returns A list of unique controllers associated with this profile. It filters out any duplicate controller
   * addresses.
   */
  public getUniqueControllers(): Controller[] {
    return this.props.controllers.filter(
      (controller, index, self) =>
        self.findIndex(c => c.equals(controller)) === index
    )
  }

  /**
   * Returns the controller address that this device uses to control the profile.
   */
  public getDeviceControllerAddress(): DeviceControllerAddress {
    return this.props.deviceControllerAddress
  }

  public static create(props: ProfileProps): Profile {
    const validatedProps = ProfilePropsSchema.parse(props)
    return new Profile(validatedProps)
  }
}
